/**
 * User Guide Content
 * Text content as constants for easy editing and maintenance
 */

export interface WorkflowStepContent {
  id: string;
  title: string;
  purpose: string;
  whatToProvide: string;
  keyConcepts?: string;
  tips: string[];
  example?: string;
}

export interface GlossaryTerm {
  term: string;
  definition: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

// What is an AI Agent section
export const whatIsAgentContent = {
  title: 'What is an AI Agent?',
  intro: `An AI agent is a software system that can understand requests, make decisions, and take actions autonomously to achieve goals. Unlike a simple chatbot that just answers questions, an agent can actually do things for you.`,
  keyPoints: [
    {
      title: 'Understands Context',
      description:
        'Agents comprehend the full picture of what you need, not just individual messages.',
    },
    {
      title: 'Makes Decisions',
      description:
        'They can reason through problems and choose the best course of action.',
    },
    {
      title: 'Takes Actions',
      description:
        'Agents can use tools, access data, and perform tasks on your behalf.',
    },
    {
      title: 'Learns & Adapts',
      description:
        'They remember context and improve their responses over time.',
    },
  ],
  examples: [
    {
      title: 'Customer Support Agent',
      description:
        'Handles customer inquiries, looks up order status, processes returns, and escalates to humans when needed.',
    },
    {
      title: 'Research Assistant',
      description:
        "Searches documents, summarizes findings, and answers questions about your company's knowledge base.",
    },
    {
      title: 'Workflow Automator',
      description:
        'Monitors events, makes decisions based on rules, and triggers actions across different systems.',
    },
  ],
  components: [
    {
      name: 'Instructions',
      description:
        'The rules and guidelines that tell the agent how to behave and what it should do.',
    },
    {
      name: 'Tools',
      description:
        'Capabilities you give the agent, like searching databases, calling APIs, or sending emails.',
    },
    {
      name: 'Memory',
      description:
        'How the agent remembers past conversations and context to provide better help.',
    },
    {
      name: 'Reasoning',
      description:
        "The AI model's ability to think through problems and make smart decisions.",
    },
  ],
};

// What this tool does section
export const whatToolDoesContent = {
  title: 'What This Tool Does',
  intro: `Agent Architect is your guide for designing AI agents. Instead of jumping straight into code, we help you think through the important decisions first.`,
  outcomes: [
    'A clear problem statement that defines exactly what your agent will do',
    'A detailed design specification covering behavior, personality, and constraints',
    'Technical architecture decisions (what AI model, what tools, how to store data)',
    'Implementation-ready code and configurations',
    'A deployment and operations plan',
  ],
  audience: [
    {
      role: 'Product Managers',
      benefit: 'Define agent requirements without needing to write code.',
    },
    {
      role: 'Developers',
      benefit: 'Get a solid foundation before implementation.',
    },
    {
      role: 'Business Analysts',
      benefit: 'Translate business needs into agent specifications.',
    },
    {
      role: 'Technical Architects',
      benefit: 'Make informed decisions about agent infrastructure.',
    },
  ],
  timeEstimate:
    'Most designs take 30 minutes to a few hours, depending on complexity.',
};

// Workflow steps content
export const workflowSteps: WorkflowStepContent[] = [
  {
    id: 'discovery',
    title: 'Step 1: Discovery',
    purpose:
      'Define what problem your agent will solve and who will benefit from it.',
    whatToProvide:
      'A description of the use case, who will use the agent, and what outcomes you want to achieve.',
    tips: [
      'Be specific about the problem - "handle customer questions about billing" is better than "help customers"',
      'Include real examples of scenarios the agent will face',
      'Think about who the users are and what they need',
      'Describe what success looks like',
    ],
    example:
      '"I want an agent that helps customer support teams answer technical questions about our software. It should search our documentation, troubleshoot common issues, and escalate complex problems to human agents."',
  },
  {
    id: 'design',
    title: 'Step 2: Design',
    purpose:
      'Define how your agent should behave, communicate, and what boundaries it should have.',
    whatToProvide:
      'Review and refine the generated problem statement. Add details about tone, personality, and constraints.',
    keyConcepts:
      'Agent persona (how it communicates), guardrails (what it should never do), edge cases (unusual situations).',
    tips: [
      "Think about edge cases - what should the agent do when it doesn't know the answer?",
      'Define clear boundaries - what should the agent refuse to do?',
      'Consider the user experience - how should conversations feel?',
      'Specify escalation paths - when should a human take over?',
    ],
  },
  {
    id: 'multiagent',
    title: 'Step 3: Multi-Agent Architecture',
    purpose:
      'Determine if your use case needs one agent or multiple specialized agents working together.',
    whatToProvide:
      'Feedback on whether to keep everything in one agent or split responsibilities among specialists.',
    keyConcepts:
      'Single agent (one AI handles everything), multi-agent (specialists that collaborate), orchestration (how agents communicate).',
    tips: [
      'Start simple - one agent is often enough for most use cases',
      'Only split into multiple agents if you have truly distinct tasks',
      'Consider that multi-agent systems add complexity',
      'Think about how agents would hand off work to each other',
    ],
  },
  {
    id: 'technical',
    title: 'Step 4: Technical Architecture',
    purpose: 'Choose the AI model, tools, and infrastructure your agent needs.',
    whatToProvide:
      'Review suggestions for model selection, tools, and data storage. Accept defaults or specify preferences.',
    keyConcepts:
      'LLM selection (which AI model), tool design (what capabilities), RAG (searching documents), memory strategy (conversation history).',
    tips: [
      'If unsure about model selection, the defaults are usually good starting points',
      'Think about what external systems your agent needs to access',
      'Consider privacy - what data should the agent have access to?',
      'Start with fewer tools and add more as needed',
    ],
  },
  {
    id: 'codegen',
    title: 'Step 5: Code Generation',
    purpose:
      'Generate implementation-ready code and configurations based on your design.',
    whatToProvide:
      'Review the generated code structure and make adjustments as needed.',
    keyConcepts:
      'Agent framework code, API integrations, configuration files, testing scaffolds.',
    tips: [
      'This creates starter code, not a complete production system',
      'Use the generated code as a foundation to build upon',
      'Review security considerations in the generated code',
      'Test thoroughly before deploying to production',
    ],
  },
  {
    id: 'operations',
    title: 'Step 6: Operations Planning',
    purpose:
      'Plan how to deploy, monitor, and maintain your agent in production.',
    whatToProvide:
      'Review infrastructure recommendations and monitoring strategies.',
    keyConcepts:
      'Deployment environment (where it runs), logging (tracking what happens), cost management (API usage), scaling (handling more users).',
    tips: [
      'Start with a simple deployment and scale up as needed',
      'Set up monitoring to track agent performance',
      'Plan for costs - AI API calls add up',
      'Have a process for reviewing and improving the agent over time',
    ],
  },
];

// Glossary terms
export const glossaryTerms: GlossaryTerm[] = [
  {
    term: 'Agent',
    definition:
      'An AI system that can understand requests, make decisions, and take actions autonomously to achieve goals.',
  },
  {
    term: 'LLM (Large Language Model)',
    definition:
      "The AI model that powers the agent's ability to understand and generate text. Examples include GPT-4, Claude, and Gemini.",
  },
  {
    term: 'Tools / Functions',
    definition:
      'Capabilities you give the agent to interact with the outside world, like searching databases, calling APIs, or sending emails.',
  },
  {
    term: 'RAG (Retrieval-Augmented Generation)',
    definition:
      'A technique where the agent searches through documents to find relevant information before generating a response.',
  },
  {
    term: 'Guardrails',
    definition:
      'Rules and constraints that prevent the agent from doing harmful, off-topic, or unwanted things.',
  },
  {
    term: 'Orchestration',
    definition:
      'How multiple agents coordinate and communicate to complete complex tasks together.',
  },
  {
    term: 'Prompt',
    definition:
      'Instructions given to an AI model to guide how it should behave and respond.',
  },
  {
    term: 'Context Window',
    definition:
      'The amount of text (past conversation, documents, etc.) that an AI model can consider at once when generating a response.',
  },
  {
    term: 'Hallucination',
    definition:
      'When an AI confidently generates information that is incorrect or made up. Good agent design helps minimize this.',
  },
  {
    term: 'Embedding',
    definition:
      'A numerical representation of text that allows AI systems to understand semantic meaning and find similar content.',
  },
  {
    term: 'Vector Database',
    definition:
      'A specialized database for storing and searching embeddings, commonly used for RAG systems.',
  },
  {
    term: 'Fine-tuning',
    definition:
      'Training an AI model on specific data to improve its performance on particular tasks.',
  },
];

// FAQ items
export const faqItems: FAQItem[] = [
  {
    question: 'Do I need to know how to code?',
    answer:
      'No coding knowledge is required for the Discovery and Design phases. Technical knowledge helps for later steps, but you can always collaborate with a developer for implementation.',
  },
  {
    question: 'How long does it take to design an agent?',
    answer:
      'Simple agents can be designed in 30 minutes. More complex agents with multiple tools or multi-agent architectures might take a few hours. Take your time - good design prevents problems later.',
  },
  {
    question: 'Can I go back and change earlier steps?',
    answer:
      'Yes! You can revisit any step at any time. Your progress is saved, and changes in earlier steps will update the subsequent designs.',
  },
  {
    question: 'What happens after I complete the workflow?',
    answer:
      "You'll have a complete agent specification including problem statement, design document, technical architecture, generated code, and operations plan. You can export these as a starting point for implementation.",
  },
  {
    question: "What if I'm not sure how to answer a question?",
    answer:
      "The AI assistant will guide you through each step. You can ask clarifying questions, and the system will suggest defaults when you're unsure. It's okay to iterate and refine your answers.",
  },
  {
    question: 'Can multiple people work on the same agent design?',
    answer:
      'Currently, agent designs are tied to individual user accounts. For team collaboration, you can export your design and share it, or have one person lead the design process while gathering input from the team.',
  },
  {
    question: 'What AI models can my agent use?',
    answer:
      'The technical architecture step helps you choose from various models like GPT-4, Claude, Gemini, and others. The best choice depends on your use case, budget, and performance requirements.',
  },
  {
    question: 'How do I know if my agent design is good?',
    answer:
      'Each step shows a quality score and provides feedback on areas to improve. Aim for a high "Design Readiness" score, but remember that real-world testing is the ultimate measure of success.',
  },
];

// Tips for success
export const successTips = [
  {
    title: 'Start with a Clear Problem',
    description:
      'The more specific you are about the problem you\'re solving, the better your agent will be. "Help customers" is vague; "Answer billing questions and process refund requests" is specific.',
  },
  {
    title: 'Think About Your Users',
    description:
      'Who will interact with this agent? What do they need? What frustrates them today? Design with empathy for the people who will use your agent.',
  },
  {
    title: 'Define Success Criteria',
    description:
      'What does success look like? How will you measure if the agent is working well? Having clear metrics helps you improve over time.',
  },
  {
    title: 'Plan for Failure Cases',
    description:
      "What should the agent do when it doesn't know the answer? When should it escalate to a human? Good agents fail gracefully.",
  },
  {
    title: 'Start Simple, Then Iterate',
    description:
      "You don't need to build a perfect agent on day one. Start with core functionality, deploy it, learn from real usage, and improve iteratively.",
  },
  {
    title: 'Test with Real Scenarios',
    description:
      'Before deploying, test your agent with realistic scenarios. Include edge cases and unusual requests to see how it handles them.',
  },
];
